import {useEffect, useState} from 'react'
import {useMutation} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useToast} from '@/hooks/use-toast'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Loader2, Plus, X} from 'lucide-react'

interface PatternSuggestDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onPatternAccepted: (pattern: string) => void
}

export function PatternSuggestDialog({
    open,
    onOpenChange,
    onPatternAccepted,
}: PatternSuggestDialogProps) {
    const services = useServices()
    const i18n = useI18n()
    const {toast} = useToast()
    const [currentSample, setCurrentSample] = useState('')
    const [samples, setSamples] = useState<string[]>([])
    const [inferredPattern, setInferredPattern] = useState<string | null>(null)

    const inferMutation = useMutation({
        mutationFn: async (samplesList: string[]) => {
            return services.utilities.inferPattern(samplesList) as Promise<{pattern: string}>
        },
        onSuccess: (data) => {
            setInferredPattern(data.pattern)
        },
        onError: () => {
            toast({
                description: i18n.attributeDefinitions.patternSuggest.error,
                variant: 'destructive',
            })
        },
    })

    const handleAddSample = () => {
        const trimmed = currentSample.trim()
        if (!trimmed) return

        if (samples.includes(trimmed)) {
            toast({
                description: i18n.attributeDefinitions.patternSuggest.duplicateSample,
                variant: 'destructive',
            })
            return
        }

        setSamples([...samples, trimmed])
        setCurrentSample('')
    }

    const handleRemoveSample = (index: number) => {
        setSamples(samples.filter((_, i) => i !== index))
        setInferredPattern(null)
    }

    // Auto-suggest when 2+ samples are added, re-infer when more samples added
    useEffect(() => {
        if (samples.length >= 2 && !inferMutation.isPending) {
            inferMutation.mutate(samples)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-suggest on sample count change, inferMutation can be stale
    }, [samples.length])

    const handleAccept = () => {
        if (inferredPattern) {
            onPatternAccepted(inferredPattern)
            handleClose()
        }
    }

    const handleClose = () => {
        setCurrentSample('')
        setSamples([])
        setInferredPattern(null)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle data-testid="pattern-suggest-title">
                        {i18n.attributeDefinitions.patternSuggest.title}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.attributeDefinitions.patternSuggest.description}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <>
                        <div className="space-y-2">
                            <Label>{i18n.attributeDefinitions.patternSuggest.addSample}</Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={currentSample}
                                        onChange={(e) => setCurrentSample(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                handleAddSample()
                                            }
                                        }}
                                        placeholder={i18n.attributeDefinitions.patternSuggest.samplePlaceholder}
                                        className="font-mono text-sm"
                                        data-testid="pattern-sample-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={handleAddSample}
                                        disabled={!currentSample.trim()}
                                        data-testid="pattern-add-sample"
                                    >
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            {samples.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label>
                                            {i18n.attributeDefinitions.patternSuggest.samplesLabel}
                                        </Label>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground">
                                                {samples.length} {i18n.attributeDefinitions.patternSuggest.samples}
                                            </span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setSamples([])
                                                    setInferredPattern(null)
                                                }}
                                                className="h-6 px-2 text-xs"
                                                data-testid="pattern-suggest-clear"
                                            >
                                                {i18n.common.clear}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="rounded-md border">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b bg-muted/50 text-xs text-muted-foreground">
                                                    <th className="px-3 py-2 text-left font-medium w-12" data-testid="pattern-col-index">#</th>
                                                    <th className="px-3 py-2 text-left font-medium" data-testid="pattern-col-value">{i18n.common.value}</th>
                                                    <th className="px-3 py-2 text-right font-medium w-12" data-testid="pattern-col-actions"></th>
                                                </tr>
                                            </thead>
                                            <tbody data-testid="pattern-samples-body">
                                                {samples.map((sample, index) => (
                                                    <tr key={index} className="border-b last:border-0 hover:bg-muted/30" data-testid={`pattern-sample-row-${index}`}>
                                                        <td className="px-3 py-2 text-xs text-muted-foreground">
                                                            {index + 1}
                                                        </td>
                                                        <td className="px-3 py-2 font-mono text-sm truncate max-w-[280px]">
                                                            {sample}
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6"
                                                                onClick={() => handleRemoveSample(index)}
                                                                data-testid={`pattern-remove-sample-${index}`}
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {samples.length === 0 && (
                                <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="pattern-empty-state">
                                    {i18n.attributeDefinitions.patternSuggest.addSampleHint}
                                </div>
                            )}
                        </>

                    {(inferredPattern || inferMutation.isPending) && (
                        <div className="space-y-2">
                            <Label>{i18n.attributeDefinitions.patternSuggest.inferredPattern}</Label>
                            {inferMutation.isPending ? (
                                <div className="flex items-center gap-2 rounded-md bg-muted/50 p-3">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">{i18n.attributeDefinitions.patternSuggest.analyzing}</span>
                                </div>
                            ) : (
                                <Input
                                    value={inferredPattern || ''}
                                    readOnly
                                    className="font-mono text-sm bg-muted"
                                    data-testid="pattern-inferred"
                                />
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} data-testid="pattern-suggest-cancel">
                        {i18n.common.cancel}
                    </Button>
                    {inferredPattern && (
                        <Button onClick={handleAccept} data-testid="pattern-suggest-accept">
                            {i18n.attributeDefinitions.patternSuggest.accept}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
